import { useState, useRef, useEffect, useCallback } from 'react';
import { useAuthenticated } from '@/hooks/useAuthenticated';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faClockRotateLeft, faWandMagicSparkles } from '@fortawesome/pro-solid-svg-icons';
import { useRouter } from 'next/navigation';
import { getSearchExecutions, launchSearchExecution } from '@/api/requests/searchExecutions';
import { getSourcePath } from '@/rewrites';
import { useAccount } from '@/hooks/useAccount';
import { Query } from '@/actions/dtos/search/query';
import { SearchExecution } from '@/actions/dtos/search/searchExecution';
import SearchDropdown from '@/components/search/SearchDropdown';
import { faCircleInfo, faCircleExclamation, faCircleCheck, faLockKeyhole } from '@fortawesome/pro-duotone-svg-icons';
import { formatDate } from '@/utils/formatDate';
import { useSelector } from 'react-redux';
import { RootState } from '@/slices/store';
import { setHasChanges, Filter } from '@/slices/features/filtersSlice';
import { SEARCH_FILTER } from '@/constants/searchFilter';
import { useSubscriptionLimits } from '@/hooks/useSubscriptionLimits';
import { useAppDispatch } from '@/slices/hooks';
import { FeatureType, openModal } from '@/slices/features/modalSlice';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import Cookies from 'js-cookie';
import SearchHistoryChip from './SearchHistoryChip';
import { getSearchSuggestions } from '@/api/requests/searchSuggestions';

type SearchInputProps = {
  query: Query | undefined;
  executionId: string | undefined;
};

const PAGE_SIZE = 10;

enum QUERY_QUALITY {
  NONE,
  POOR,
  AVERAGE,
  GOOD,
}

enum DROPDOWN_STATE {
  HISTORY,
  SUGGESTIONS,
}

interface QueryQuality {
  icon: IconDefinition;
  color: string;
  borderColor: string;
}

const QUERY_QUALITY_INDICATOR: Record<QUERY_QUALITY, QueryQuality> = {
  [QUERY_QUALITY.NONE]: { icon: faCircleExclamation, color: 'transparent', borderColor: 'transparent' }, // mocked icon
  [QUERY_QUALITY.POOR]: { icon: faCircleExclamation, color: 'oklch(var(--wa))', borderColor: 'oklch(var(--wa))' },
  [QUERY_QUALITY.AVERAGE]: { icon: faCircleInfo, color: 'oklch(var(--in))', borderColor: 'oklch(var(--in))' },
  [QUERY_QUALITY.GOOD]: { icon: faCircleCheck, color: 'oklch(var(--su))', borderColor: 'transparent' },
};

function evaluateSearchQuality(searchQuery: string): QUERY_QUALITY {
  if (!searchQuery || searchQuery.trim().length === 0) {
    return QUERY_QUALITY.NONE;
  }

  const normalizedQuery = searchQuery.trim();
  // Split on spaces, hyphens, commas and other common separators
  const words = normalizedQuery.split(/[\s\-\,\.\/\(\)\[\]\{\}\+]+/).filter((word) => word.length > 0);

  if (words.length === 0) {
    return QUERY_QUALITY.NONE;
  }

  let weightedWordCount = 0;

  for (const word of words) {
    if (word.length <= 4) {
      weightedWordCount += 0.5;
    } else if (word.length <= 8) {
      weightedWordCount += 1;
    } else if (word.length <= 14) {
      weightedWordCount += 1.5;
    } else {
      weightedWordCount += 2;
    }
  }

  if (weightedWordCount < 2) {
    return QUERY_QUALITY.POOR;
  } else if (weightedWordCount < 3 || weightedWordCount > 10) {
    return QUERY_QUALITY.AVERAGE;
  } else {
    return QUERY_QUALITY.GOOD;
  }
}

const SearchInput = ({ query, executionId }: SearchInputProps) => {
  const { isAuthenticated } = useAuthenticated();
  const [description, setDescription] = useState<string>(query?.description || '');
  const dropdownRef = useRef(null);
  const router = useRouter();
  const { isSearchLimitReached } = useSubscriptionLimits();
  const [isSearchLoading, setIsSearchLoading] = useState<boolean>(false);
  const { refreshCustomer } = useAccount();
  const [isSearchActive, setIsSearchActive] = useState<boolean>(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
  const observer = useRef<IntersectionObserver>(undefined);
  const [isDropdownLoading, setIsDropdownLoading] = useState<boolean>(false);
  const [searchExecutions, setSearchExecutions] = useState<SearchExecution[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const filters = useSelector((state: RootState) => state.filters);
  const [queryQuality, setQueryQuality] = useState<QUERY_QUALITY>(QUERY_QUALITY.NONE);
  const dispatch = useAppDispatch();
  const [queryQualityWarningCookie, setQueryQualityWarningCookie] = useState<string | undefined>(
    Cookies.get('bidpilot_query_quality_warning_seen'),
  );
  const [dropdownState, setDropdownState] = useState<DROPDOWN_STATE>(DROPDOWN_STATE.HISTORY);
  const [isSuggesterActive, setIsSuggesterActive] = useState<boolean>(false);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);

  useEffect(() => {
    if (!description || !description.trim()) {
      setQueryQuality(QUERY_QUALITY.NONE);
      return;
    }
    setQueryQuality(evaluateSearchQuality(description));
  }, [description]);

  useEffect(() => {
    if (query) {
      setDescription(query.description);
    }
  }, [query]);

  useEffect(() => {
    if (!executionId) {
      setDescription('');
    }
  }, [executionId]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDescription(event.target.value);
  };

  useEffect(() => {
    if (isSearchActive && isAuthenticated) {
      const fetchSearchExecutions = async () => {
        setIsDropdownLoading(true);
        const executions = await getSearchExecutions(page, PAGE_SIZE);
        if (page === 0) {
          setSearchExecutions(executions.content);
        } else {
          setSearchExecutions((prev) => [...prev, ...executions.content]);
        }
        setHasMore(executions.content.length === PAGE_SIZE);
        setIsDropdownLoading(false);
      };

      fetchSearchExecutions();
    }
  }, [page, isSearchActive, isAuthenticated]);

  useEffect(() => {
    if (isSuggesterActive && isAuthenticated) {
      const fetchSearchSuggestions = async () => {
        setIsDropdownLoading(true);
        const suggestions = await getSearchSuggestions(description);
        setSearchSuggestions(suggestions);
        setIsDropdownLoading(false);
        setIsSuggesterActive(false);
      };

      fetchSearchSuggestions();
    }
  }, [isSuggesterActive, isAuthenticated, description]);

  const handleSearch = useCallback(
    async (descriptionOverride?: string) => {
      if (!descriptionOverride) {
        descriptionOverride = description;
      }

      if (isSearchLimitReached) {
        dispatch(openModal(FeatureType.MORE_SEARCH_REQUESTS));
        return;
      }

      if (queryQuality === QUERY_QUALITY.POOR && !queryQualityWarningCookie) {
        dispatch(openModal(FeatureType.SEARCH_INDICATOR));
        setQueryQualityWarningCookie(Cookies.set('bidpilot_query_quality_warning_seen', 'true', { expires: 30 }));
        return;
      }

      if (!descriptionOverride.trim()) {
        return;
      }

      setIsSearchLoading(true);
      const data: { [key: string]: any } = { description: descriptionOverride };
      Object.keys(filters)
        .filter((key) => key !== 'area')
        .forEach((key) => {
          const filter = filters[key];
          if (filter) {
            if (Array.isArray(filter)) {
              data[key] = (filter as Filter[]).map((item) => item.value);
            } else {
              const singleFilter = filter as Filter;
              if (typeof singleFilter.value !== 'undefined') {
                data[singleFilter.name!] = singleFilter.value;
              }
            }
          }
        });

      if (filters.nutsCode) {
        let nutsCodes = filters.nutsCode.map((code) => code.value);
        if (nutsCodes.includes(SEARCH_FILTER.UNKNOWN)) {
          nutsCodes = nutsCodes.filter((code) => code !== SEARCH_FILTER.UNKNOWN);
          data.includeEmptyNutsCodes = true;
        }
        data.nutsCode = nutsCodes;
      }

      if (filters.cpvCode) {
        let cpvCodes = filters.cpvCode.map((code) => code.value);
        if (cpvCodes.includes(SEARCH_FILTER.UNKNOWN)) {
          cpvCodes = cpvCodes.filter((code) => code !== SEARCH_FILTER.UNKNOWN);
          data.includeEmptyCpvCodes = true;
        }
        data.cpvCode = cpvCodes;
      }

      if (filters.area) {
        data.includeEmptyLocations = filters.area.includeEmptyLocations;
        data.radius = filters.area.radius;
        data.point = filters.area.coordinates.map((location) => `${location.lng},${location.lat}`);
      }

      if (filters.budget) {
        data.minBudget = filters.budget.minBudget;
        data.maxBudget = filters.budget.maxBudget;
        data.includeUnknownBudget = filters.budget.includeUnknownBudget;
      }

      const response = await launchSearchExecution(data, !isAuthenticated).finally(() => setIsSearchLoading(false));
      const searchExecutionId = response.id;
      if (isAuthenticated) {
        refreshCustomer();
      }
      router.push(getSourcePath('/search') + `/${searchExecutionId}`);
      setSearchExecutions([response as SearchExecution, ...searchExecutions]);
    },
    [
      isSearchLimitReached,
      description,
      filters,
      isAuthenticated,
      refreshCustomer,
      router,
      dispatch,
      queryQuality,
      queryQualityWarningCookie,
      searchExecutions,
    ],
  );

  useEffect(() => {
    if (filters.hasChanges) {
      handleSearch();
      dispatch(setHasChanges(false));
    }
  }, [filters.hasChanges, dispatch, handleSearch]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && description.trim()) {
      event.preventDefault();
      handleSearch();
    }
  };

  const loadMoreExecutions = useCallback(async () => {
    if (!hasMore || isSearchLoading) return;
    setPage((prevPage) => prevPage + 1);
  }, [hasMore, isSearchLoading]);

  const lastExecutionElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (isSearchLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && hasMore) {
          loadMoreExecutions();
        }
      });
      if (node) observer.current.observe(node);
    },
    [isSearchLoading, hasMore, loadMoreExecutions],
  );

  const handleExecutionClick = (execution: SearchExecution) => {
    router.push(getSourcePath('/search') + '/' + execution.id);
    setIsDropdownOpen(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    setDescription(suggestion);
    setIsDropdownOpen(false);
    handleSearch(suggestion);
  };

  const handleClockButtonClick = async () => {
    setDropdownState(DROPDOWN_STATE.HISTORY);
    setPage(0);
    setIsSearchActive(true);
    setIsDropdownOpen(true);
    setIsDropdownLoading(true);
  };

  const handleSuggestionsButtonClick = async () => {
    setDropdownState(DROPDOWN_STATE.SUGGESTIONS);
    setSearchSuggestions([]);
    setIsDropdownOpen(true);
    setIsSuggesterActive(true);
    setIsDropdownLoading(true);
  };

  const handleSearchIndicatorButtonClick = () => {
    dispatch(openModal(FeatureType.SEARCH_INDICATOR));
    setQueryQualityWarningCookie(Cookies.set('bidpilot_query_quality_warning_seen', 'true', { expires: 30 }));
  };

  return (
    <div className="relative w-full max-w-xl flex-grow" ref={dropdownRef}>
      <div className="flex flex-row items-center">
        <div
          style={{
            borderColor: QUERY_QUALITY_INDICATOR[queryQuality].borderColor,
            width: 'clamp(15rem, 50vw, 36rem)',
          }}
          className="input input-bordered relative flex h-9 items-center gap-1 border-2 text-sm text-primary "
        >
          {isSearchLoading ? (
            <span className="loading loading-spinner h-4 w-4" />
          ) : (
            <span className={`${!description.trim() ? 'cursor-not-allowed' : 'cursor-pointer'} relative`}>
              <FontAwesomeIcon onClick={() => handleSearch()} icon={faSearch} className={`h-4 w-4 text-primary`} />
              {isSearchLimitReached && (
                <FontAwesomeIcon
                  icon={faLockKeyhole}
                  className="fa-fw absolute -right-1.5 -top-1 h-3 w-3 text-accent"
                />
              )}
            </span>
          )}
          <input
            type="search"
            className="mr-4 grow overflow-ellipsis"
            placeholder="Beschreibung der Leistungen nach denen Sie suchen..."
            value={description}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            maxLength={1000}
            enterKeyHint="search"
            lang="de"
          />
          <div className="flex gap-1">
            <button
              type="button"
              className="flex h-6 w-6 items-center justify-center rounded-full"
              disabled={queryQuality === QUERY_QUALITY.NONE}
              onClick={handleSearchIndicatorButtonClick}
            >
              <FontAwesomeIcon
                icon={QUERY_QUALITY_INDICATOR[queryQuality].icon}
                className="h-6 w-6"
                style={{ color: QUERY_QUALITY_INDICATOR[queryQuality].color }}
              />
            </button>
          </div>
        </div>
        {isAuthenticated && (
          <>
            <button
              type="button"
              className="border-1 ml-1 flex h-5 w-5 items-center justify-center rounded-lg border border-primary-content bg-primary p-4 max-xs:hidden sm:ml-2 sm:h-9 sm:w-9"
              onClick={handleSuggestionsButtonClick}
            >
              <FontAwesomeIcon
                icon={faWandMagicSparkles}
                swapOpacity
                className="h-5 w-5 text-primary-content [--fa-primary-opacity:0.35] [--fa-primary-opacity:0.85]"
              />
            </button>
            <button
              type="button"
              className="border-1 ml-1 flex h-5 w-5 items-center justify-center rounded-lg border border-primary-content bg-primary p-4 max-xs:hidden sm:ml-2 sm:h-9 sm:w-9"
              onClick={handleClockButtonClick}
            >
              <FontAwesomeIcon icon={faClockRotateLeft} className="h-4 w-4 text-primary-content" />
            </button>
          </>
        )}
      </div>
      <SearchDropdown isOpen={isDropdownOpen} onClose={() => setIsDropdownOpen(false)}>
        <div className={`max-h-[256px] overflow-y-auto`}>
          {dropdownState === DROPDOWN_STATE.HISTORY && (
            <>
              {searchExecutions.length === 0 && !isDropdownLoading && (
                <div className="mb-2 text-center">Noch keine Suchanfragen ausgeführt.</div>
              )}
              <>
                {searchExecutions.map((execution, index) => (
                  <div
                    ref={searchExecutions.length === index + 1 ? lastExecutionElementRef : null}
                    key={`${execution.id}-${index}`}
                    onClick={() => handleExecutionClick(execution)}
                    className={`group mx-2 mb-1 flex cursor-pointer flex-col gap-1 rounded-btn p-2 py-2 ${
                      executionId === execution.id
                        ? 'bg-primary text-primary-content'
                        : 'bg-base-100 text-primary hover:bg-base-200'
                    }`}
                  >
                    <div className="text-sm font-normal">{execution.query.description}</div>
                    <div className="flex flex-row flex-wrap gap-x-5 text-nowrap text-xs font-light max-md:mb-1 max-md:gap-x-1 max-md:gap-y-1">
                      <SearchHistoryChip currentExecutionId={executionId} hoveredExecutionId={execution.id}>
                        {formatDate(execution.updated)} -{' '}
                        {new Date(execution.updated).toLocaleTimeString([], {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false,
                        })}
                      </SearchHistoryChip>
                      <SearchHistoryChip currentExecutionId={executionId} hoveredExecutionId={execution.id}>
                        {execution.matchingTenders} {execution.matchingTenders === 1 ? 'Ergebnis' : 'Ergebnisse'}
                      </SearchHistoryChip>
                      <SearchHistoryChip
                        currentExecutionId={executionId}
                        hoveredExecutionId={execution.id}
                        className="max-md:order-2 "
                      >
                        Minimum {execution.query.minScore}%
                      </SearchHistoryChip>
                      <SearchHistoryChip
                        currentExecutionId={executionId}
                        hoveredExecutionId={execution.id}
                        className="max-md:order-1"
                      >
                        {(() => {
                          const nonBudgetFields = [
                            'contractors',
                            'includeEmptyLocations',
                            'nutsCodes',
                            'includeEmptyNutsCodes',
                            'cpvCodes',
                            'includeEmptyCpvCodes',
                            'publicationBeginSinceDays',
                            'submissionEndInDays',
                            'scope',
                            'area',
                          ] as (keyof Query)[];

                          const nonBudgetCount = nonBudgetFields.reduce((acc, field) => {
                            const value = execution.query[field];
                            // if it's an array, add 1 only if not empty, otherwise add 1 only if filter is specified
                            return acc + (Array.isArray(value) ? Math.min(value.length, 1) : value ? 1 : 0);
                          }, 0);

                          const hasBudgetFilter = Boolean(
                            execution.query.minBudget ||
                              execution.query.maxBudget ||
                              execution.query.includeUnknownBudget,
                          );

                          return nonBudgetCount + (hasBudgetFilter ? 1 : 0);
                        })()}{' '}
                        Filter
                      </SearchHistoryChip>
                    </div>
                  </div>
                ))}
                {isDropdownLoading && <div className="loading loading-spinner mx-auto mb-2 flex items-center" />}
              </>
            </>
          )}

          {dropdownState === DROPDOWN_STATE.SUGGESTIONS && (
            <>
              {searchSuggestions.length === 0 && !isDropdownLoading && (
                <div className="mb-2 text-center">Noch keine Suchempfehlungen ausgeführt.</div>
              )}
              {searchSuggestions.map((suggestion, index) => (
                <div
                  key={index}
                  onClick={() => handleSuggestionClick(suggestion)}
                  className={`mx-2 mb-1 flex cursor-pointer flex-col rounded p-2 py-1 hover:bg-base-200`}
                >
                  <div className="text-sm font-normal">{suggestion}</div>
                </div>
              ))}
              {isDropdownLoading && <div className="loading loading-spinner mx-auto mb-2 flex items-center" />}
            </>
          )}
        </div>
      </SearchDropdown>
    </div>
  );
};

export default SearchInput;
